import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

/**
 * Two-tab toggle in the topbar that flips between the standard portal
 * (Dashboard, Catalog, Orders, …) and the IMP-internal Tools launcher.
 * Only rendered for IMP-internal users — the AppShell gates the mount via
 * a tm:access permission check.
 *
 * The label and the tooltip are separate keys on purpose. They used to share
 * one: `t("nav.portal", "Portal")` reads as "short label, long text only as a
 * fallback", but i18next's second argument is the default for a MISSING key —
 * and nav.portal exists in every locale carrying the long form, so the tab
 * rendered "Portal — customer-facing modules" and the intended short label was
 * dead code. nav.tools had the same problem in three Tools breadcrumbs.
 */
export function PortalToolsToggle() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const onTools = pathname.startsWith("/tools");

  return (
    <div role="tablist" aria-label={t("portalTools.workspace", "Workspace")} style={wrap}>
      <Link
        to="/"
        role="tab"
        aria-selected={!onTools}
        style={tab(!onTools)}
        title={t("nav.portalHint")}
      >
        {t("nav.portal")}
      </Link>
      <Link
        to="/tools"
        role="tab"
        aria-selected={onTools}
        style={tab(onTools)}
        title={t("nav.toolsHint")}
      >
        {t("nav.tools")}
      </Link>
    </div>
  );
}

const wrap: React.CSSProperties = {
  display: "inline-flex",
  background: "var(--surface-2)",
  border: "1px solid var(--border)",
  borderRadius: 8,
  padding: 3,
  gap: 2,
};

function tab(active: boolean): React.CSSProperties {
  return {
    padding: "5px 14px",
    borderRadius: 6,
    fontSize: 12.5,
    fontWeight: 600,
    textDecoration: "none",
    color: active ? "#fff" : "var(--ink-2)",
    background: active ? "var(--imp-primary)" : "transparent",
    transition: "background .12s ease, color .12s ease",
    lineHeight: "20px",
  };
}
